import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Unsubscribe as UnsubscribeIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { subscriberApi } from '../../../api/marketing';
import { Subscriber, SubscriberStats } from '../../../types/marketing.types';
import { getClients } from '../../../api/clients';

const SubscribersPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [stats, setStats] = useState<SubscriberStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState<Subscriber | null>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    leadId: '',
    tags: [] as string[],
    preferences: {
      newsletters: true,
      productUpdates: true,
      promotions: false
    }
  });

  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await subscriberApi.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        status: statusFilter || undefined
      });

      if (response.success && response.data) {
        setSubscribers(response.data);
        setTotalCount(response.pagination?.total || 0);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch subscribers', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, statusFilter, enqueueSnackbar]);

  useEffect(() => {
    fetchSubscribers();
    fetchStats();
  }, [fetchSubscribers]);

  const fetchClients = async () => {
    try {
      const clientsData = await getClients();
      console.log('Fetched clients:', clientsData);
      setClients(clientsData);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      enqueueSnackbar('Failed to load clients', { variant: 'error' });
      setClients([]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await subscriberApi.getStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleOpenDialog = (subscriber?: Subscriber) => {
    if (subscriber) {
      setEditingSubscriber(subscriber);
      setFormData({
        email: subscriber.email,
        firstName: subscriber.firstName || '',
        lastName: subscriber.lastName || '',
        leadId: subscriber.leadId || '',
        tags: subscriber.tags,
        preferences: subscriber.preferences
      });
    } else {
      setEditingSubscriber(null);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        leadId: '',
        tags: [],
        preferences: {
          newsletters: true,
          productUpdates: true,
          promotions: false
        }
      });
    }
    fetchClients();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSubscriber(null);
  };

  const handleSave = async () => {
    try {
      if (editingSubscriber) {
        await subscriberApi.update(editingSubscriber._id, formData);
        enqueueSnackbar('Subscriber updated successfully', { variant: 'success' });
      } else {
        await subscriberApi.create(formData);
        enqueueSnackbar('Subscriber created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchSubscribers();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to save subscriber', { variant: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) {
      return;
    }

    try {
      await subscriberApi.delete(id);
      enqueueSnackbar('Subscriber deleted successfully', { variant: 'success' });
      fetchSubscribers();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to delete subscriber', { variant: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'UNSUBSCRIBED':
        return 'default';
      case 'BOUNCED':
        return 'error';
      case 'COMPLAINED':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Subscribers</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Subscriber
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Total Subscribers
                    </Typography>
                    <Typography variant="h4">{stats.totalSubscribers}</Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Active
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {stats.activeSubscribers}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Unsubscribed
                    </Typography>
                    <Typography variant="h4">{stats.unsubscribedCount}</Typography>
                  </Box>
                  <UnsubscribeIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Last 30 Days
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {stats.recentSubscribers}
                    </Typography>
                  </Box>
                  <AddIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="UNSUBSCRIBED">Unsubscribed</MenuItem>
                <MenuItem value="BOUNCED">Bounced</MenuItem>
                <MenuItem value="COMPLAINED">Complained</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Subscribers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Subscribed</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : subscribers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No subscribers found
                </TableCell>
              </TableRow>
            ) : (
              subscribers.map((subscriber) => (
                <TableRow key={subscriber._id}>
                  <TableCell>{subscriber.email}</TableCell>
                  <TableCell>
                    {subscriber.firstName || subscriber.lastName
                      ? `${subscriber.firstName || ''} ${subscriber.lastName || ''}`.trim()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={subscriber.status}
                      color={getStatusColor(subscriber.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{subscriber.source}</TableCell>
                  <TableCell>
                    {subscriber.tags.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {subscriber.tags.slice(0, 2).map((tag) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))}
                        {subscriber.tags.length > 2 && (
                          <Chip label={`+${subscriber.tags.length - 2}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(subscriber.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(subscriber)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(subscriber._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            backgroundImage: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%)'
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)'
            }
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          {editingSubscriber ? 'Edit Subscriber' : 'Add Subscriber'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={!!editingSubscriber}
              sx={{
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' }
                }
              }}
            />
            <TextField
              label="First Name"
              fullWidth
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              sx={{
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' }
                }
              }}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              sx={{
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' }
                }
              }}
            />
            <Autocomplete
              options={clients}
              getOptionLabel={(option) => `${option.companyName || option.contactName} (${option.email})`}
              value={clients.find((c: any) => c._id === formData.leadId) || null}
              onChange={(_, newValue) => setFormData({ ...formData, leadId: newValue?._id || '' })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Link to Client (Optional)"
                  placeholder="Search for a client..."
                  sx={{
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: 'white' }
                    },
                    '& .MuiAutocomplete-popupIndicator': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiAutocomplete-clearIndicator': { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    backgroundColor: '#1e293b',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '& .MuiAutocomplete-listbox': {
                      backgroundColor: '#1e293b',
                      '& .MuiAutocomplete-option': {
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(59, 130, 246, 0.4)'
                        },
                        '&[aria-selected="true"]': {
                          backgroundColor: 'rgba(59, 130, 246, 0.6)',
                          '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.7)'
                          }
                        }
                      }
                    },
                    '& .MuiAutocomplete-noOptions': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      backgroundColor: '#1e293b'
                    }
                  }
                }
              }}
            />
            <TextField
              label="Tags (comma separated)"
              fullWidth
              value={formData.tags.join(', ')}
              onChange={(e) => setFormData({
                ...formData,
                tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
              })}
              helperText="Enter tags separated by commas"
              sx={{
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' }
                },
                '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
              }}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'white' }}>
                Email Preferences
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.preferences.newsletters}
                    onChange={(e) => setFormData({
                      ...formData,
                      preferences: { ...formData.preferences, newsletters: e.target.checked }
                    })}
                    sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: 'white' } }}
                  />
                }
                label="Newsletters"
                sx={{ color: 'white' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.preferences.productUpdates}
                    onChange={(e) => setFormData({
                      ...formData,
                      preferences: { ...formData.preferences, productUpdates: e.target.checked }
                    })}
                    sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: 'white' } }}
                  />
                }
                label="Product Updates"
                sx={{ color: 'white' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.preferences.promotions}
                    onChange={(e) => setFormData({
                      ...formData,
                      preferences: { ...formData.preferences, promotions: e.target.checked }
                    })}
                    sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: 'white' } }}
                  />
                }
                label="Promotions"
                sx={{ color: 'white' }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.email}>
            {editingSubscriber ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscribersPage;
